import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Image, Video } from 'lucide-react';

export type ImageUseCase = 'static-image' | 'video-thumbnail';

interface ImageUseCaseSelectorProps {
  value: ImageUseCase;
  onChange: (useCase: ImageUseCase) => void;
}

/**
 * ImageUseCaseSelector - Select the intended use case for the generated image
 *
 * Use Cases:
 * - Static Image: For emails, social media posts (supports 1:1, 4:5, 16:9)
 * - Video Thumbnail: For video cover images (only 9:16 portrait)
 *
 * Styled to match generation mode selectors with centered icons
 */
export const ImageUseCaseSelector: React.FC<ImageUseCaseSelectorProps> = ({
  value,
  onChange
}) => {
  return (
    <div className="space-y-2">
      <Label>Use Case</Label>
      <RadioGroup
        value={value}
        onValueChange={(val) => onChange(val as ImageUseCase)}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        {/* Static Image */}
        <div>
          <RadioGroupItem
            value="static-image"
            id="static-image"
            className="peer sr-only"
          />
          <Label
            htmlFor="static-image"
            className="flex flex-col items-center justify-center space-y-3 rounded-lg border-2 border-muted p-6 cursor-pointer hover:border-muted-foreground/40 transition-all peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/10 h-full min-h-[140px]"
          >
            <Image className="h-12 w-12 text-muted-foreground" />
            <div className="text-center space-y-1">
              <p className="font-semibold">Static Image</p>
              <p className="text-xs text-muted-foreground">
                For email campaigns and social media posts. Choose from multiple aspect ratios.
              </p>
            </div>
          </Label>
        </div>

        {/* Video Thumbnail */}
        <div>
          <RadioGroupItem
            value="video-thumbnail"
            id="video-thumbnail"
            className="peer sr-only"
          />
          <Label
            htmlFor="video-thumbnail"
            className="flex flex-col items-center justify-center space-y-3 rounded-lg border-2 border-muted p-6 cursor-pointer hover:border-muted-foreground/40 transition-all peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/10 h-full min-h-[140px]"
          >
            <Video className="h-12 w-12 text-muted-foreground" />
            <div className="text-center space-y-1">
              <p className="font-semibold">Video Thumbnail</p>
              <p className="text-xs text-muted-foreground">
                For video cover images. Fixed 9:16 portrait format.
              </p>
            </div>
          </Label>
        </div>
      </RadioGroup>
      <p className="text-xs text-muted-foreground">
        Select how you plan to use this image
      </p>
    </div>
  );
};

export default ImageUseCaseSelector;
